import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { formatMoney } from "@theme/money-formatting";

/**
 * Live "Add {Format} · $XX.XX" amount inside the add-to-cart button label.
 *
 * Lives INSIDE the ATC button so product-form's whole-button morph re-renders the
 * server-side verb/amount on variant change (then `updatedCallback` recomputes with the
 * current quantity). The "Added ✓" swap is pure CSS keyed on the button's existing
 * `data-added` attribute — this component only owns qty × price math.
 *
 * @extends {Component<{}>}
 */
class AtcPriceComponent extends Component {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {number} */
  #quantity = 1;

  /** @param {Event} event */
  #onQuantityUpdate = event => {
    const customEvent = /** @type {import("@theme/events").QuantitySelectorUpdateEvent} */ (event);
    const quantity = Number(customEvent.detail?.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) {
      return;
    }
    this.#quantity = quantity;
    this.#recompute();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const target = this.closest(".shopify-section, dialog");
    target?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, {
      signal: this.#controller.signal,
    });
    this.#readQuantityFromForm();
    this.#recompute();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    // Fires after product-form morphs the button on variant change: dataset.price is the new
    // variant's server-rendered price; reapply the current quantity.
    this.#readQuantityFromForm();
    this.#recompute();
  }

  #readQuantityFromForm() {
    const quantityInput = this.closest("product-form-component")?.querySelector('input[ref="quantityInput"]');
    if (quantityInput instanceof HTMLInputElement) {
      const quantity = Number(quantityInput.value);
      if (Number.isFinite(quantity) && quantity >= 1) {
        this.#quantity = quantity;
      }
    }
  }

  #recompute() {
    const unitPrice = Number(this.dataset.price);
    if (!Number.isFinite(unitPrice)) {
      return;
    }
    const amountTarget = this.querySelector("[data-atc-amount]");
    if (!amountTarget) {
      return;
    }
    const format = this.dataset.moneyFormat || "{{amount}}";
    amountTarget.textContent = formatMoney(unitPrice * this.#quantity, format, this.dataset.currency || "");
  }
}

if (!customElements.get("atc-price-component")) {
  customElements.define("atc-price-component", AtcPriceComponent);
}

export { AtcPriceComponent };
